import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { ProjectionProviderType } from "@app/projects/enumerators";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { ProviderProfileActionEnum, ServicingAccessPoint } from "@app/projects/network-assessment/provider-hub/interfaces";
import { ProjectionPanelMode, ProjectionProviderDetails } from "@app/projects/projection-provider";
import projectionProviderDetailsReducer, {
  projectionProviderDetailsInitialState,
  ProjectionProviderIdentifier,
} from "@app/projects/projection-provider/projectionProviderDetailsReducer";
import { AccessPointForProjection } from "@app/projects/results/projection-provider";
import { TerminatePanelMode, WrappedTerminateProvidersPanel } from "@app/projects/servicing-provider/TerminateProvidersPanel";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { FC, useCallback, useContext, useReducer } from "react";
import { showProviderHubActionToast } from "@app/projects/network-assessment/provider-hub/utility";

interface HospitalProfileActionFlyoutProps {
  accessPoints: ServicingAccessPoint[];
  excludedAccessPointsCount?: number;
  onSubmit: () => void;
  providerAction: ProviderProfileActionEnum;
  targetedAccessPointsCount?: number;
}

const HospitalProfileActionFlyout: FC<HospitalProfileActionFlyoutProps> = ({
  accessPoints,
  excludedAccessPointsCount = 0,
  onSubmit,
  providerAction,
  targetedAccessPointsCount = 0,
}) => {
  const projectContext = useContext(CountyProjectContext);
  const providerHubContext = useContext(ProviderHubContext);

  const [projectionProvidersState, projectionProvidersReducer] = useReducer(
    projectionProviderDetailsReducer,
    projectionProviderDetailsInitialState
  );

  const { selectedHospital } = providerHubContext;

  const getProjectionProviderType = (type: ProviderProfileActionEnum): ProjectionProviderType => {
    switch (type) {
      case ProviderProfileActionEnum.Exclude: return ProjectionProviderType.ExcludeImpact;
      case ProviderProfileActionEnum.Target: return ProjectionProviderType.Target;
      default: return ProjectionProviderType.Unknown;
    }
  };

  // De-duplicate by NPI so each unique service provider gets one action record, and tag every access point with the
  // hospital CCN. The action is recorded once against the CCN and expanded to the hospital's services at process time,
  // so a single entry per provider is sufficient here.
  const accessPointsForAction: AccessPointForProjection[] = Array.from(
    new Map(accessPoints.map(ap => [ap.npi, ap])).values()
  ).map(ap => ({
    ccn: selectedHospital?.ccn,
    npi: ap.npi,
    providerId: ap.providerId,
    providerNetworkType: ap.providerNetworkType,
    specialtyGroupCode: ap.specialtyGroupCode,
    tin: ap.tin,
  } as AccessPointForProjection));

  const onProjectionProvidersCreated = useCallback(async (runProjection: boolean, hasUnprocessedProviders = true) => {
    if (runProjection) {
      await projectContext.processProviderChanges(false);
    } else {
      projectContext.setProjectHasUnprocessedProjectionProviders(hasUnprocessedProviders, false, getProjectionProviderType(providerAction));
    }
    showProviderHubActionToast("Hospital", providerAction, !runProjection);
    onSubmit();
  }, [projectContext, onSubmit, providerAction]);

  const renderActionAlertOrInfoBox = () => {
    if (providerAction === ProviderProfileActionEnum.Target && excludedAccessPointsCount > 0) {
      return (
        <QESAlert type="warning" persist>
          <p className="demibold mt-2 mb-2">Some access points are currently excluded. Target actions will only apply to access points without an action status. To target excluded access points, remove the Exclusion.</p>
        </QESAlert>
      );
    }

    if (providerAction === ProviderProfileActionEnum.Exclude && targetedAccessPointsCount > 0) {
      return (
        <QESAlert type="warning" persist>
          <p className="demibold mt-2 mb-2">Some access points have been targeted. Applying the Exclusion will override the existing Target rule.</p>
        </QESAlert>
      );
    }

    return (
      <div className="provider-profile-target-flyout__info-box">
        <p>{selectedHospital?.hospitalName ?? "---"}: All Specialties, All Locations</p>
      </div>
    );
  };

  return (
    <div>
      <p>Please enter the necessary information to {providerAction.toLocaleLowerCase()} the available providers for your network.</p>
      {renderActionAlertOrInfoBox()}
      {providerAction === ProviderProfileActionEnum.Terminate ? (
        <WrappedTerminateProvidersPanel
          projectId={projectContext.project.projectId}
          onSubmit={() => {
            projectContext.setProjectHasUnprocessedTerminations(true);
            showProviderHubActionToast("Hospital", ProviderProfileActionEnum.Terminate, true);
            onSubmit();
          }}
          crossSpecialtyAndNetwork={{
            allLocations: true,
            allProjects: false,
            allSpecialties: true,
            projectionBy: ProjectionProviderIdentifier.CCN,
          }}
          terminatePanelMode={TerminatePanelMode.Create}
          selectedProviders={accessPointsForAction}
          showAffiliationsInsights={projectContext.project.showAffiliationsInsights}
          hideAllLocationsOption
          hideAllSpecialtiesOption
        />
      ) : (
        <ProjectionProviderDetails
          accessPoints={accessPointsForAction}
          crossSpecialtyAndNetworkType={{
            allLocations: true,
            allProjects: false,
            allSpecialties: true,
            projectionBy: ProjectionProviderIdentifier.CCN,
          }}
          showAffiliationsInsights={projectContext.project.showAffiliationsInsights}
          formState={projectionProvidersState}
          formDispatch={projectionProvidersReducer}
          hasProvidersWithoutNPI={false}
          networkAssessmentAnalysisType={projectContext.runConfiguration.networkAssessmentAnalysisType}
          projectId={projectContext.project.projectId}
          projectionPanelMode={ProjectionPanelMode.Create}
          projectionProviderType={getProjectionProviderType(providerAction)}
          providerNetworkType={selectedHospital?.providerNetworkTypes[0] ?? ProviderNetworkTypeEnum.Network}
          specialtyGroupCode=""
          isZipProject={false}
          isRunning={projectContext.project?.isRunning}
          hideProviderInfoSection
          onProjectionProvidersCreated={onProjectionProvidersCreated}
          hideAllLocationsOption
          hideAllSpecialtiesOption
          hideAllProjectsOption
        />
      )}
    </div>
  );
};

export default HospitalProfileActionFlyout;
